<!-- eslint-disable -->
<template>
  <teleport to="body">
    <div
      v-if="showModal"
      ref="modalContainer"
      class="video-call-modal"
      :class="{
        'is-maximized': isMaximized,
        'is-fullscreen': isFullscreen,
        'is-dragging': isDragging,
      }"
      :style="modalStyle"
    >
      <div
        class="modal-header"
        @mousedown="startDrag"
        @dblclick="toggleMaximize"
      >
        <div class="header-content">
          <div class="call-info">
            <span class="call-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                  fill="#4CAF50"
                  class="recording-dot"
                />
              </svg>
            </span>
            <span class="call-title">{{ videoCallTitle }}</span>
          </div>
          <div
            v-if="connecting || reconnecting || error"
            class="call-status"
            :class="{
              'is-connecting': connecting,
              'is-reconnecting': reconnecting,
              'has-error': error,
            }"
          >
            <span v-if="connecting">Connecting…</span>
            <span v-else-if="reconnecting">Reconnecting…</span>
            <span v-else>{{ error }}</span>
          </div>
        </div>
      </div>

      <VideoCall
        :conversation-id="conversationId"
        :event-id="eventId"
        :can-moderate-room="canModerateRoom"
        :is-broadcasting="isBroadcasting"
        :participant-count="participantCount"
        :call-title="callTitle"
        :can-close-manually="canCloseManually"
        :show-call="showModal"
        :show-pop-controls="showPopControls"
        is-in-modal
        @pop-in="$emit('pop-in')"
        @toggle-chat="$emit('toggle-chat')"
        @close="handleClose"
      />

      <div
        v-for="handle in resizeHandles"
        :key="handle"
        :class="`resize-handle resize-${handle}`"
        @mousedown="startResize($event, handle)"
      />
    </div>
  </teleport>
</template>
<script>
import { mapActions, mapState } from "pinia";
import { useLivekitStore } from "%/apps/video-engine/stores/livekit-store";
import VideoCall from "%/apps/conversations/components/message/VideoCall.vue";
import { useVideoCallStore } from "%/apps/video-engine/stores/video-call-store";

export default {
  name: "VideoCallModal",
  components: { VideoCall },
  props: {
    conversationId: {
      type: Number,
      required: true,
    },
    showModal: {
      type: Boolean,
      required: true,
      default: false,
    },
    canCloseManually: {
      type: Boolean,
      required: false,
      default: true,
    },
    eventId: {
      type: [Number, String],
      required: false,
      default: null,
    },
    canModerateRoom: {
      type: Boolean,
      required: false,
      default: false,
    },
    isBroadcasting: {
      type: Boolean,
      required: false,
      default: false,
    },
    showPopControls: {
      type: Boolean,
      required: false,
      default: true,
    },
    participantCount: {
      type: Number,
      required: false,
      default: 0,
    },
    callTitle: {
      type: String,
      required: false,
      default: "",
    },
  },
  emits: ["close", "pop-in", "toggle-chat"],
  data() {
    const modalPreferences = this.getVideoModalPreferences();

    return {
      isMaximized: modalPreferences.isMaximized,
      isFullscreen: modalPreferences.isFullscreen,
      isDragging: false,
      isResizing: false,
      dragStart: { pointerX: 0, pointerY: 0, modalX: 0, modalY: 0 },
      dragAnimationFrame: null,
      modalPos: modalPreferences.modalPos,
      modalSize: modalPreferences.modalSize,
      resizeHandle: null,
      startBox: null,
      hasFinalizedClose: false,
      pendingClose: false,
      resizeHandles: ["n", "s", "e", "w", "ne", "nw", "se", "sw"],
    };
  },
  computed: {
    ...mapState(useVideoCallStore, [
      "activeVideoCallChannel",
    ]),
    ...mapState(useLivekitStore, {
      connected: "isConnected",
      connecting: "isConnecting",
      reconnecting: "isReconnecting",
      error: state => state.connection.error,
    }),
    modalStyle() {
      if (this.isFullscreen || this.isMaximized) {
        return {};
      }
      return {
        left: `${this.modalPos.x}px`,
        top: `${this.modalPos.y}px`,
        width: `${this.modalSize.w}px`,
        height: `${this.modalSize.h}px`,
      };
    },
    videoCallTitle() {
      let channelName = this.activeVideoCallChannel?.channel_name || "Video Call";
      if (channelName !== "Video Call") {
        channelName = "# " + channelName;
      }
      return `${channelName}`;
    },
  },
  watch: {
    showModal: {
      immediate: true,
      handler(val) {
        if (val) {
          this._ensureConnected();
        } else {
          this.disconnect();
        }
      },
    },
  },
  mounted() {
    window.addEventListener("mouseup", this._stopPointerOps, { passive: true });
    window.addEventListener("mousemove", this._onWindowMouseMove, {
      passive: false,
    });
  },
  unmounted() {
    this.disconnect();
    window.removeEventListener("mouseup", this._stopPointerOps);
    window.removeEventListener("mousemove", this._onWindowMouseMove);
  },
  methods: {
    ...mapActions(useVideoCallStore, [
      "updateVideoModalPreferences",
      "getVideoModalPreferences",
    ]),
    ...mapActions(useLivekitStore, [
      "connect",
      "disconnect",
    ]),
    async _ensureConnected() {
      if (!this.conversationId) {
        return;
      }
      try {
        await this.connect(this.conversationId);
      } catch (error) {
        // connection errors surface through store state
      }
    },
    toggleMaximize() {
      this.isMaximized = !this.isMaximized;
      if (this.isMaximized) {
        this.isFullscreen = false;
      }

      this.updateVideoModalPreferences({
        ...this.getVideoModalPreferences(),
        isMaximized: this.isMaximized,
      });
    },
    handleClose() {
      if (this.connected || this.connecting) {
        this.leaveCall();
      } else {
        this._finalizeClose();
      }
    },
    async leaveCall() {
      this.pendingClose = true;
      try {
        await this.disconnect();
        if (!this.connected && !this.connecting) {
          this.pendingClose = false;
          this._finalizeClose();
        }
      } catch (err) {
        this.pendingClose = false;
        this._finalizeClose();
        throw err;
      }
    },
    startDrag(e) {
      if (this.isFullscreen || this.isMaximized) {
        return;
      }
      this.isDragging = true;
      this.dragStart = {
        pointerX: e.clientX,
        pointerY: e.clientY,
        modalX: this.modalPos.x,
        modalY: this.modalPos.y,
      };
      if (this.dragAnimationFrame) {
        cancelAnimationFrame(this.dragAnimationFrame);
        this.dragAnimationFrame = null;
      }
      e.preventDefault();
    },
    startResize(e, handle) {
      if (this.isFullscreen || this.isMaximized) {
        return;
      }
      this.isResizing = true;
      this.resizeHandle = handle;
      this.startBox = {
        x: this.modalPos.x,
        y: this.modalPos.y,
        w: this.modalSize.w,
        h: this.modalSize.h,
        mx: e.clientX,
        my: e.clientY,
      };
      e.preventDefault();
    },
    _onWindowMouseMove(e) {
      if (this.isDragging) {
        if (this.dragAnimationFrame) {
          cancelAnimationFrame(this.dragAnimationFrame);
        }
        const dx = e.clientX - this.dragStart.pointerX;
        const dy = e.clientY - this.dragStart.pointerY;
        const nextX = this.dragStart.modalX + dx;
        const nextY = this.dragStart.modalY + dy;
        this.dragAnimationFrame = requestAnimationFrame(() => {
          this.modalPos.x = nextX;
          this.modalPos.y = nextY;
          this._keepInViewport();
          this.dragAnimationFrame = null;
        });
        if (typeof e.preventDefault === "function") {
          e.preventDefault();
        }
      } else if (this.isResizing) {
        const dx = e.clientX - this.startBox.mx;
        const dy = e.clientY - this.startBox.my;
        const minW = 520,
          minH = 340;
        let { x, y, w, h } = this.startBox;

        if (this.resizeHandle.includes("e")) {
          w = Math.max(minW, this.startBox.w + dx);
        }
        if (this.resizeHandle.includes("s")) {
          h = Math.max(minH, this.startBox.h + dy);
        }
        if (this.resizeHandle.includes("w")) {
          const newW = Math.max(minW, this.startBox.w - dx);
          x = this.startBox.x + (this.startBox.w - newW);
          w = newW;
        }
        if (this.resizeHandle.includes("n")) {
          const newH = Math.max(minH, this.startBox.h - dy);
          y = this.startBox.y + (this.startBox.h - newH);
          h = newH;
        }

        this.modalPos = { x, y };
        this.modalSize = { w, h };
        this._keepInViewport();
      }
    },
    _stopPointerOps() {
      if (this.dragAnimationFrame) {
        cancelAnimationFrame(this.dragAnimationFrame);
        this.dragAnimationFrame = null;
      }
      this.isDragging = false;
      this.isResizing = false;

      this.updateVideoModalPreferences({
        ...this.getVideoModalPreferences(),
        modalPos: this.modalPos,
        modalSize: this.modalSize,
      });

      this._keepInViewport();
    },
    _keepInViewport() {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const pad = 8;
      this.modalPos.x = Math.min(
        Math.max(this.modalPos.x, pad),
        vw - this.modalSize.w - pad,
      );
      this.modalPos.y = Math.min(
        Math.max(this.modalPos.y, pad),
        vh - this.modalSize.h - pad,
      );
    },
    _clearAllMediaElements() {
      const mediaEls = this.$el?.querySelectorAll?.("video, audio") || [];
      mediaEls.forEach(el => {
        if (el.srcObject) {
          el.srcObject = null;
        }
        el.removeAttribute?.("src");
        el.load?.();
      });
    },
    _finalizeClose() {
      if (this.hasFinalizedClose) {
        return;
      }
      this.hasFinalizedClose = true;
      this.pendingClose = false;
      this._clearAllMediaElements();
      this.isMaximized = false;
      this.isFullscreen = false;
      this.$emit("close");
    },
  },
  styleGuide: () => ({
    modalBackground: { "color.background": "paper" },
    modalBorderWidth: { "size.border": "standard" },
    modalBorderColor: { "color.border": "standard" },
    modalBorderRadius: { "size.borderRadius": "large" },
  }),
};
</script>

<style scoped>
.video-call-modal {
  position: fixed;
  background: v-bind("$getStyles.modalBackground");
  border: v-bind("$getStyles.modalBorderWidth") solid v-bind("$getStyles.modalBorderColor");
  border-radius: v-bind("$getStyles.modalBorderRadius");
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  animation: modalSlide 0.3s ease-out;
  z-index: 9999;
}

@keyframes modalSlide {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.video-call-modal.is-dragging {
  cursor: move;
}

.video-call-modal.is-maximized {
  width: calc(100vw - 40px) !important;
  height: calc(100vh - 40px) !important;
  left: 20px !important;
  top: 20px !important;
}

.video-call-modal.is-minimized {
  height: 45px !important;
  width: 280px !important;
}

.video-call-modal.is-minimized .modal-body,
.video-call-modal.is-minimized .resize-handle {
  display: none;
}

.video-call-modal.is-fullscreen {
  width: 100vw !important;
  height: 100vh !important;
  left: 0 !important;
  top: 0 !important;
  border-radius: 0;
}

.modal-header {
  height: 45px;
  background: v-bind("$getStyles.modalBackground");
  border-radius: 10px 10px 0 0;
  cursor: move;
  user-select: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.video-call-modal.is-fullscreen .modal-header {
  border-radius: 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 0 14px;
}

.call-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
}

.call-status.is-connecting {
  color: #1976d2;
}

.call-status.is-reconnecting {
  color: #f57c00;
}

.call-status.has-error {
  color: #d32f2f;
}

.call-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.call-icon {
  display: flex;
  align-items: center;
}

.recording-dot {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.call-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.header-controls {
  display: flex;
  gap: 6px;
}

.header-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.header-btn:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #333;
}

.close-btn:hover {
  background: rgba(244, 67, 54, 0.1);
  color: #f44336;
}

.video-call-modal.is-fullscreen .modal-body {
  border-radius: 0;
}

.no-media-content h3 {
  color: #333;
  font-size: 20px;
  margin-bottom: 6px;
  font-weight: 600;
}

.no-media-content p {
  color: #666;
  font-size: 13px;
}

.connection-indicator i {
  width: 2px;
  height: 6px;
  background: #4caf50;
  border-radius: 1px;
  transition: all 0.2s ease;
}

.connection-indicator i:nth-child(2) {
  height: 9px;
}

.connection-indicator i:nth-child(3) {
  height: 12px;
}

.connection-indicator.medium i {
  background: #ff9800;
}

.connection-indicator.medium i:nth-child(3) {
  opacity: 0.3;
}

.connection-indicator.poor i {
  background: #f44336;
}

.connection-indicator.poor i:nth-child(2),
.connection-indicator.poor i:nth-child(3) {
  opacity: 0.3;
}

.resize-handle {
  position: absolute;
  user-select: none;
}

.resize-n {
  top: 0;
  left: 8px;
  right: 8px;
  height: 6px;
  cursor: ns-resize;
}

.resize-s {
  bottom: 0;
  left: 8px;
  right: 8px;
  height: 6px;
  cursor: ns-resize;
}

.resize-e {
  right: 0;
  top: 8px;
  bottom: 8px;
  width: 6px;
  cursor: ew-resize;
}

.resize-w {
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 6px;
  cursor: ew-resize;
}

.resize-ne {
  top: 0;
  right: 0;
  width: 10px;
  height: 10px;
  cursor: nesw-resize;
}

.resize-nw {
  top: 0;
  left: 0;
  width: 10px;
  height: 10px;
  cursor: nwse-resize;
}

.resize-se {
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  cursor: nwse-resize;
}

.resize-sw {
  bottom: 0;
  left: 0;
  width: 10px;
  height: 10px;
  cursor: nesw-resize;
}

</style>
